import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useConvertTaskToCost } from "@/hooks/use-budget-assignments";
import { formatNOK } from "@/lib/format";
import type { BudgetLineRow } from "@/hooks/use-project-detail";

interface Props {
  bl: BudgetLineRow;
  projectId: string;
}

/**
 * "Konverter til kostnad"-knapp for interne Oppgave-linjer. Inverse av
 * ConvertCostButton i BudgetCostsSection. Sletter alle assignments og setter
 * cost_type='external'. Bruker spesifiserer estimert kostnad (vår kostnad
 * mot leverandør) — amount (salgssum) beholdes uendret.
 */
export function ConvertTaskToCostButton({ bl, projectId }: Props) {
  const [open, setOpen] = useState(false);
  const [costInput, setCostInput] = useState("0");
  const convertMut = useConvertTaskToCost();

  const amount = bl.amount ?? 0;
  const assignmentCount = bl.assignments.length;

  function openDialog() {
    setCostInput("0");
    setOpen(true);
  }

  function handleConvert() {
    const cost = Number(costInput);
    if (!Number.isFinite(cost) || cost < 0) return;
    convertMut.mutate(
      { budgetLineId: bl.id, projectId, estimatedCost: cost },
      { onSuccess: () => setOpen(false) },
    );
  }

  const costValid = Number.isFinite(Number(costInput)) && Number(costInput) >= 0;

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        disabled={convertMut.isPending}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-border text-muted-foreground rounded hover:bg-muted transition-colors disabled:opacity-50"
      >
        Konverter til kostnad
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Konverter til kostnad</DialogTitle>
            <DialogDescription>
              "{bl.description}" blir en kostnad-linje.{" "}
              {assignmentCount > 0 && (
                <>
                  <strong>{assignmentCount} tildeling{assignmentCount === 1 ? "" : "er"}</strong>{" "}
                  fjernes.{" "}
                </>
              )}
              Beløp ({formatNOK(amount)}) beholdes som salgssum.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            <Label htmlFor="convert-cost-input">Innkjøpspris / vår kostnad (kr)</Label>
            <Input
              id="convert-cost-input"
              type="number"
              min={0}
              step={100}
              value={costInput}
              onChange={(e) => setCostInput(e.target.value)}
              autoFocus
            />
            <p className="text-[12px] text-muted-foreground">
              Sett til <strong>0</strong> hvis dette er en intern kostnad (f.eks. utleie
              av eget utstyr — ingen direkte kostnad mot leverandør). Sett et beløp
              hvis det er en ekstern kostnad (innleid leverandør, lisens). Brukes til
              GEEK-beregning.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={convertMut.isPending}>
              Avbryt
            </Button>
            <Button onClick={handleConvert} disabled={!costValid || convertMut.isPending}>
              {convertMut.isPending ? "Konverterer..." : "Konverter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
